// AdminPanel/ZitadelFinder — the Search tab's "Zitadel" lookup: search IDENTITY
// directly by current email, phone, or org id.
//
// Why this exists next to the Org ID and Email tabs, which look similar:
// they search OUR tables through ListOrganizationsV3 and answer "which customer
// has this SIGNUP contact". This searches zitadel and answers "who is this USER
// and which org are they in". A login email lives in zitadel, one row per user,
// and an org routinely has more than one — so a customer who changed their
// address after signup is invisible to the other tabs and found here.
//
// Measured on mini: dharmadev@combirds.com is a zitadel user and matches ZERO
// signup contacts, so no spelling of the V3 search finds it.
//
// It also recovers something the V3 list gave up. V3 reads `tenants`, so an org
// whose onboarding died before that row was written does not appear in the
// customer list at all. Searching by org id here finds it, and
// hasCustomerRecord=false names the condition instead of an empty result that
// reads as "no such org".
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Search} from '@mui/icons-material';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
  MenuItem, TextField, Typography
} from '@mui/material';

import {adminSearchIdentity} from '../../actions/adminPanel';

// The request is discriminated — exactly one field — so the UI picks the mode
// explicitly rather than guessing from what was typed. Guessing is how
// "8417841425" becomes an org-id lookup that silently finds nothing.
const MODES = [
  {
    key: 'email',
    label: 'Email',
    placeholder: 'customer@example.com',
    hint: 'The user\'s CURRENT login email in zitadel — not the address captured at signup.'
  },
  {
    key: 'phone',
    label: 'Phone',
    placeholder: '9876543210',
    hint: 'Indian numbers assumed when no country code is given. Punctuation is ignored.'
  },
  {
    key: 'orgId',
    label: 'Org ID',
    placeholder: '378884552388509999',
    hint: 'Resolves even when the org has no customer record — the signature of an onboarding that did not finish.'
  }
];

class ZitadelFinder extends Component {
  state = {mode: 'email', query: '', searching: false, results: null, error: null};

  // Guards against a stale response landing after a newer search (or
  // unmount) — only the latest request may write state.
  searchSeq = 0;

  componentWillUnmount() {
    this.searchSeq++;
  }

  activeMode() {
    return MODES.find((m) => m.key === this.state.mode) || MODES[0];
  }

  search = async () => {
    const mode = this.activeMode();
    const query = (this.state.query || '').trim();
    if (!query) {
      this.setState({error: `Enter ${mode.label.toLowerCase()} to search.`, results: null});
      return;
    }
    // Whitespace inside is a paste artefact for all three — none of email,
    // phone or org id legitimately contains one.
    if (/\s/.test(query) && mode.key !== 'phone') {
      this.setState({error: `A${mode.key === 'orgId' ? 'n org id' : 'n email address'} contains no spaces — check the value pasted.`, results: null});
      return;
    }

    const seq = ++this.searchSeq;
    this.setState({searching: true, results: null, error: null});
    try {
      const matches = await adminSearchIdentity({[mode.key]: query});
      if (this.searchSeq !== seq) return;
      this.setState({searching: false, results: matches});
    } catch (err) {
      if (this.searchSeq !== seq) return;
      const status = err?.response?.status;
      const msg = status === 403
        ? 'You don\'t have permission to use admin search.'
        : err?.response?.data?.message || err?.message || 'Search failed — please try again.';
      this.setState({searching: false, error: msg});
    }
  };

  openOrg = (m) => {
    const loaded = this.props.orgs?.find((x) => x.instituteId === m.orgId);
    this.props.onOpenOrg(loaded || {instituteId: m.orgId, id: m.orgId, name: m.orgName || m.orgId});
  };

  renderResults() {
    const {results} = this.state;
    if (!results) return null;
    if (results.length === 0) {
      return <Alert severity="info">No match in zitadel for that value.</Alert>;
    }
    return results.map((m) => (
      <Card key={m.userId || m.orgId} sx={{border: '1px solid #c8e6c9', mb: 1}}>
        <CardContent>
          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap'}}>
            <Box sx={{minWidth: 0}}>
              <Typography variant="h6">{m.orgName || m.orgId}</Typography>
              {m.displayName && (
                <Typography variant="body2" color="text.secondary">{m.displayName}</Typography>
              )}
              {m.email && <Typography variant="body2" color="text.secondary">{m.email}</Typography>}
              {m.phone && <Typography variant="body2" color="text.secondary">{m.phone}</Typography>}
              <Typography variant="caption" color="text.secondary" component="div">
                Org: <code>{m.orgId}</code>
                {m.userId ? <> · User: <code>{m.userId}</code></> : null}
              </Typography>
            </Box>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0}}>
              {/* An org with no customer record cannot be opened in the
                  details dialog — there is nothing behind it. Say so instead
                  of offering a button that leads nowhere. */}
              {m.hasCustomerRecord
                ? (
                  <Button variant="contained" onClick={() => this.openOrg(m)}>
                    Open Org Details
                  </Button>
                )
                : (
                  <Chip
                    size="small"
                    color="warning"
                    label="No customer record — onboarding did not finish"
                  />
                )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    ));
  }

  render() {
    const {mode, query, searching, error} = this.state;
    const active = this.activeMode();
    return (
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
          Searches zitadel directly. Use this when the customer gives you the
          email or phone they log in with — the other tabs match the contact
          captured at signup, which may be a different address.
        </Typography>
        <Box sx={{display: 'flex', gap: 1, mb: 1}}>
          <TextField
            select
            size="small"
            label="Search by"
            value={mode}
            onChange={(e) => this.setState({mode: e.target.value, results: null, error: null})}
            sx={{minWidth: 140, flexShrink: 0}}
            disabled={searching}
          >
            {MODES.map((m) => <MenuItem key={m.key} value={m.key}>{m.label}</MenuItem>)}
          </TextField>
          <TextField
            fullWidth
            size="small"
            label={active.label}
            placeholder={active.placeholder}
            value={query}
            onChange={(e) => this.setState({query: e.target.value, error: null})}
            onKeyDown={(e) => { if (e.key === 'Enter') this.search(); }}
            disabled={searching}
          />
          <Button
            variant="contained"
            onClick={this.search}
            disabled={searching}
            startIcon={searching ? <CircularProgress size={16} color="inherit" /> : <Search />}
            sx={{flexShrink: 0}}
          >
            Search
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 2}}>
          {active.hint}
        </Typography>
        {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
        {this.renderResults()}
      </Box>
    );
  }
}

ZitadelFinder.propTypes = {
  orgs: PropTypes.array,
  onOpenOrg: PropTypes.func.isRequired
};

export default ZitadelFinder;
